import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Trash2, Save, Eye, RefreshCw, X, Copy, GripVertical, FileJson, ExternalLink } from 'lucide-react';
import InlineNameEditor from './ui/InlineNameEditor';
import CompactSelectPicker from './ui/CompactSelectPicker';
import SyntaxHighlightInput from './ui/SyntaxHighlightInput';
import ResizableSplitter from './ui/ResizableSplitter';
import CodeEditor from './ui/CodeEditor';
import { Modal } from './ui/Modal';
import { useSidebarDrag } from '../hooks/useSidebarDrag';
import { isModKey } from '../utils/keyboard';
import { generateSchemaMock } from '../utils/generateSchemaMock';
import { parseJsonToSchemaTree } from '../utils/jsonToSchemaTree';
import {
  SCHEMA_NODE_TYPES,
  makeSchemaNode,
  nodeHasChildren,
  updateNode,
  deleteNode,
  addSiblingNode,
  addChildNode,
  duplicateNode,
  moveSchemaNodeRelative,
  findSchemaNode,
  getSchemaItem,
} from '../utils/schemaTree';

const NODE_DND_PREFIX = 'schema-node-';
const nodeDndId = (id) => `${NODE_DND_PREFIX}${id}`;
const parseNodeDndId = (raw) =>
  raw && raw.startsWith(NODE_DND_PREFIX) ? raw.slice(NODE_DND_PREFIX.length) : null;

const TYPE_OPTIONS = SCHEMA_NODE_TYPES.map((t) => ({ value: t, label: t }));
const ROOT_TYPE_OPTIONS = [
  { value: 'object', label: 'object' },
  { value: 'array', label: 'array' },
];

/**
 * Nested table editor for a Smart Mock schema. Each row is a field
 * (name / type / value / description) and object|array rows expand into children.
 * The value cell reuses SyntaxHighlightInput so {{variables}} and {{$random*}} work.
 */
const SchemaEditor = ({
  initialData,
  schemas,
  onDataChange,
  onSave,
  onOpenSchemaRef,
  onRegisterRenameShortcut,
  onSchemaNameCommit,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  currentWorkspaceId,
  onVariableUpdate,
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState(initialData?.name || 'Schema');
  const [rootType, setRootType] = useState(initialData?.rootType === 'array' ? 'array' : 'object');
  const [nodes, setNodes] = useState(() =>
    Array.isArray(initialData?.nodes) ? initialData.nodes : []
  );
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewJson, setPreviewJson] = useState('{}');
  const [splitLayoutTick, setSplitLayoutTick] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [editingSchemaName, setEditingSchemaName] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const nameSnapshotRef = useRef(initialData?.name || 'Schema');

  const schemaItem = useMemo(
    () => getSchemaItem(schemas, initialData?.schemaId),
    [schemas, initialData?.schemaId]
  );

  const folderPath = useMemo(() => {
    const folders = schemas?.folders || [];
    const byId = new Map(folders.map((f) => [String(f.id), f]));
    const chain = [];
    let current = schemaItem?.folder_id ? byId.get(String(schemaItem.folder_id)) : null;
    const guard = new Set();
    while (current && !guard.has(String(current.id))) {
      guard.add(String(current.id));
      const label = current.name?.trim();
      if (label) chain.unshift(label);
      current = current.parent_id ? byId.get(String(current.parent_id)) : null;
    }
    return chain;
  }, [schemas, schemaItem]);

  const breadcrumbDisplayLabel = name?.trim() ? name : t('schemaEditor.schemaName');
  const breadcrumbWidthSource = editingSchemaName
    ? (renameDraft || name || '')
    : breadcrumbDisplayLabel;
  const nameSlotWidthCh = Math.min(Math.max(breadcrumbWidthSource.length, 12), 48);

  const buildResolveVar = useCallback(() => {
    const isEnabled = (item, key) => item?.enabled !== false && item?.key === key;
    return (name) => {
      const key = String(name).trim();
      const env = selectedEnvironment?.variables?.find((v) => isEnabled(v, key));
      if (env) return env.value;
      const ws = workspaceVariables?.find((v) => isEnabled(v, key));
      if (ws) return ws.value;
      const g = globalVariables?.find((v) => isEnabled(v, key));
      if (g) return g.value;
      return null;
    };
  }, [selectedEnvironment, workspaceVariables, globalVariables]);

  const generatePreview = useCallback(() => {
    try {
      const data = generateSchemaMock({
        rootType,
        nodes,
        schemas,
        resolveVar: buildResolveVar(),
      });
      return JSON.stringify(data, null, 2);
    } catch (err) {
      return JSON.stringify({ error: err?.message || 'Preview failed' }, null, 2);
    }
  }, [rootType, nodes, schemas, buildResolveVar]);

  const refreshPreview = useCallback(() => {
    setPreviewJson(generatePreview());
  }, [generatePreview]);

  const togglePreview = useCallback(() => {
    setPreviewOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (previewOpen) {
      refreshPreview();
      setSplitLayoutTick((n) => n + 1);
    }
  }, [previewOpen, rootType, nodes, refreshPreview]);

  // Re-hydrate when switching to a different schema tab.
  const tabIdRef = useRef(initialData?.id);
  useEffect(() => {
    if (tabIdRef.current === initialData?.id) return;
    tabIdRef.current = initialData?.id;
    setName(initialData?.name || 'Schema');
    setRootType(initialData?.rootType === 'array' ? 'array' : 'object');
    setNodes(Array.isArray(initialData?.nodes) ? initialData.nodes : []);
    setCollapsed(new Set());
    setEditingSchemaName(false);
  }, [initialData?.id, initialData?.name, initialData?.rootType, initialData?.nodes]);

  useEffect(() => {
    if (editingSchemaName) return;
    setName(initialData?.name || 'Schema');
  }, [initialData?.name, editingSchemaName]);

  const change = useCallback(
    (patch) => onDataChange?.({ name, rootType, nodes, ...patch }),
    [onDataChange, name, rootType, nodes]
  );

  const startSchemaNameEdit = useCallback(() => {
    nameSnapshotRef.current = name;
    setRenameDraft(name ?? '');
    setEditingSchemaName(true);
  }, [name]);

  const commitSchemaNameEdit = useCallback(
    (trimmed) => {
      const snapshot = nameSnapshotRef.current ?? name;
      setEditingSchemaName(false);
      setRenameDraft('');

      if (!trimmed) {
        const prev = snapshot || initialData?.name || 'Schema';
        setName(prev);
        change({ name: prev });
        return;
      }

      if (trimmed !== name) {
        setName(trimmed);
        change({ name: trimmed });
      }

      if (trimmed !== (snapshot || '').trim()) {
        onSchemaNameCommit?.(trimmed, (snapshot || '').trim());
      }
    },
    [name, initialData?.name, onSchemaNameCommit, change]
  );

  const cancelSchemaNameEdit = useCallback(() => {
    const prev = nameSnapshotRef.current ?? name;
    setName(prev);
    change({ name: prev });
    setEditingSchemaName(false);
    setRenameDraft('');
  }, [name, change]);

  useEffect(() => {
    onRegisterRenameShortcut?.(startSchemaNameEdit);
    return () => onRegisterRenameShortcut?.(null);
  }, [onRegisterRenameShortcut, startSchemaNameEdit]);

  const commitNodes = useCallback(
    (nextNodes) => {
      setNodes(nextNodes);
      change({ nodes: nextNodes });
    },
    [change]
  );

  // ── Drag-and-drop reordering of field rows ──
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const [activeDragId, setActiveDragId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null); // { overId, position }

  const handleNodeDragStart = useCallback((activeId) => {
    setDropIndicator(null);
    setActiveDragId(activeId);
  }, []);

  const handleNodeDragMove = useCallback((activeId, overId, position) => {
    if (!overId || overId === activeId || !position) {
      setDropIndicator(null);
      return;
    }
    const dragId = parseNodeDndId(activeId);
    const targetId = parseNodeDndId(overId);
    if (!dragId || !targetId) {
      setDropIndicator(null);
      return;
    }
    // Hide the indicator for illegal drops (target inside the dragged subtree).
    const next = moveSchemaNodeRelative(nodesRef.current, dragId, targetId, position);
    if (next === nodesRef.current) {
      setDropIndicator(null);
      return;
    }
    setDropIndicator({ overId, position });
  }, []);

  const handleNodeDragEnd = useCallback(
    (activeId, overId, position) => {
      setActiveDragId(null);
      setDropIndicator(null);
      if (!overId || overId === activeId || !position) return;
      const dragId = parseNodeDndId(activeId);
      const targetId = parseNodeDndId(overId);
      if (!dragId || !targetId) return;
      const next = moveSchemaNodeRelative(nodesRef.current, dragId, targetId, position);
      if (next !== nodesRef.current) commitNodes(next);
    },
    [commitNodes]
  );

  const handleNodeDragCancel = useCallback(() => {
    setActiveDragId(null);
    setDropIndicator(null);
  }, []);

  const { getHandleProps, overlayRef, dragX, dragY } = useSidebarDrag({
    onDragStart: handleNodeDragStart,
    onDragMove: handleNodeDragMove,
    onDragEnd: handleNodeDragEnd,
    onDragCancel: handleNodeDragCancel,
  });

  const draggedNodeName = useMemo(() => {
    const id = parseNodeDndId(activeDragId);
    if (!id) return null;
    return findSchemaNode(nodesRef.current, id)?.name || '';
  }, [activeDragId]);

  const schemaRefOptions = useMemo(
    () =>
      (schemas?.items || [])
        .filter((it) => String(it.id) !== String(initialData?.schemaId))
        .map((it) => ({ value: it.id, label: it.name || 'Schema' })),
    [schemas, initialData?.schemaId]
  );

  const toggleCollapse = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const variableProps = {
    selectedEnvironment,
    workspaceVariables,
    globalVariables,
    collectionVariables: [],
    onVariableUpdate,
    variableContext: { workspaceId: currentWorkspaceId },
  };

  const handleSave = useCallback(() => {
    onSave?.({ schemaId: initialData?.schemaId, name, rootType, nodes });
  }, [onSave, initialData?.schemaId, name, rootType, nodes]);

  const handleImport = useCallback(() => {
    try {
      const { rootType: importedRoot, nodes: importedNodes } = parseJsonToSchemaTree(importText);
      setRootType(importedRoot);
      setNodes(importedNodes);
      change({ rootType: importedRoot, nodes: importedNodes });
      setImportOpen(false);
      setImportText('');
      setImportError('');
    } catch (err) {
      setImportError(err?.message || 'Invalid JSON');
    }
  }, [importText, change]);

  const openRefSchema = useCallback(
    (refId) => {
      if (!refId || !onOpenSchemaRef) return;
      const item = getSchemaItem(schemas, refId);
      if (item) onOpenSchemaRef(item);
    },
    [onOpenSchemaRef, schemas]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isModKey(e) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const renderRows = (list, depth) =>
    (list || []).map((node) => {
      const hasKids = nodeHasChildren(node);
      const isCollapsed = collapsed.has(node.id);
      const rawId = nodeDndId(node.id);
      const isDragging = activeDragId === rawId;
      const ind = dropIndicator?.overId === rawId ? dropIndicator : null;
      const dropBefore = ind?.position === 'before';
      const dropAfter = ind?.position === 'after';
      return (
        <React.Fragment key={node.id}>
          {dropBefore && <div className="schema-drop-line" aria-hidden />}
          <div
            className="schema-row"
            data-drag-id={rawId}
            style={{ opacity: isDragging ? 0.4 : undefined }}
          >
            <div className="schema-cell-name" style={{ paddingLeft: 4 + depth * 18 }}>
              <GripVertical
                className="schema-drag-handle"
                size={18}
                role="button"
                tabIndex={-1}
                aria-label={t('schemaEditor.reorder', 'Drag to reorder')}
                {...getHandleProps(rawId)}
              />
              <button
                type="button"
                className="schema-collapse-btn"
                onClick={() => hasKids && toggleCollapse(node.id)}
                style={{ visibility: hasKids ? 'visible' : 'hidden' }}
                aria-label={isCollapsed ? t('schemaEditor.expand') : t('schemaEditor.collapse')}
              >
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              <input
                type="text"
                className="mock-input"
                value={node.name}
                placeholder={t('schemaEditor.fieldName')}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => commitNodes(updateNode(nodes, node.id, { name: e.target.value }))}
              />
            </div>

            <div className="schema-cell-type">
              <CompactSelectPicker
                value={node.type}
                options={TYPE_OPTIONS}
                aria-label={t('schemaEditor.type')}
                onChange={(v) => {
                  let next = updateNode(nodes, node.id, { type: v });
                  // Becoming an object/array: seed it with one empty child field.
                  if (v === 'object' || v === 'array') {
                    const updated = findSchemaNode(next, node.id);
                    if (!updated?.children?.length) {
                      next = addChildNode(next, node.id, makeSchemaNode());
                    }
                    setCollapsed((prev) => {
                      const n = new Set(prev);
                      n.delete(node.id);
                      return n;
                    });
                  }
                  commitNodes(next);
                }}
              />
            </div>

            <div className="schema-cell-value">
              {node.type === 'object' || node.type === 'array' ? (
                <span className="schema-value-muted">{`{ ${node.type} }`}</span>
              ) : node.type === 'ref' ? (
                <div className="schema-ref-cell">
                  <CompactSelectPicker
                    value={node.refSchemaId || ''}
                    options={
                      schemaRefOptions.length
                        ? schemaRefOptions
                        : [{ value: '', label: t('schemaEditor.noSchemas') }]
                    }
                    aria-label={t('schemaEditor.refSchema')}
                    onChange={(v) => commitNodes(updateNode(nodes, node.id, { refSchemaId: v }))}
                  />
                  {node.refSchemaId && onOpenSchemaRef && (
                    <button
                      type="button"
                      className="mock-iconbtn schema-ref-open"
                      title={t('schemaEditor.openRef')}
                      onClick={() => openRefSchema(node.refSchemaId)}
                    >
                      <ExternalLink size={14} />
                    </button>
                  )}
                </div>
              ) : (
                <SyntaxHighlightInput
                  value={node.value}
                  placeholder={t('schemaEditor.valuePlaceholder')}
                  className="request-builder-kv-input w-full rounded-md focus:outline-none"
                  style={{ minHeight: '32px' }}
                  onChange={(e) => commitNodes(updateNode(nodes, node.id, { value: e.target.value }))}
                  {...variableProps}
                  showDynamicVarPicker
                />
              )}
            </div>

            <div className="schema-cell-desc">
              <input
                type="text"
                className="mock-input"
                value={node.description}
                placeholder={t('schemaEditor.description')}
                onChange={(e) =>
                  commitNodes(updateNode(nodes, node.id, { description: e.target.value }))
                }
              />
            </div>

            <div className="schema-cell-actions">
              <button
                type="button"
                className="mock-iconbtn"
                title={t('schemaEditor.addSibling')}
                onClick={() => commitNodes(addSiblingNode(nodes, node.id, makeSchemaNode()))}
              >
                <Plus size={14} />
              </button>
              <button
                type="button"
                className="mock-iconbtn"
                title={t('schemaEditor.duplicate')}
                onClick={() => commitNodes(duplicateNode(nodes, node.id))}
              >
                <Copy size={14} />
              </button>
              <button
                type="button"
                className="mock-iconbtn mock-iconbtn--danger"
                title={t('schemaEditor.delete')}
                onClick={() => commitNodes(deleteNode(nodes, node.id))}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          {dropAfter && <div className="schema-drop-line" aria-hidden />}
          {hasKids && !isCollapsed && renderRows(node.children, depth + 1)}
        </React.Fragment>
      );
    });

  const tableContent = (
    <div className="schema-table">
      {nodes.length === 0 ? (
        <div className="schema-empty">{t('schemaEditor.empty')}</div>
      ) : (
        renderRows(nodes, 0)
      )}

      <div className="schema-add-root">
        <button
          type="button"
          className="mock-add-btn"
          onClick={() => commitNodes([...nodes, makeSchemaNode()])}
        >
          <Plus size={14} /> {t('schemaEditor.addField')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="schema-editor flex flex-col h-full min-h-0 bg-white">
      <div className="schema-editor-header">
        <div className="flex items-center min-w-0 flex-1 gap-2 text-xs">
          {folderPath.map((segment, idx) => (
            <React.Fragment key={`${segment}-${idx}`}>
              <span className="text-gray-500 truncate flex-shrink-0 max-w-[10rem]" title={segment}>
                {segment}
              </span>
              <ChevronRight
                size={14}
                strokeWidth={2}
                className="text-gray-400 flex-shrink-0 opacity-90"
                aria-hidden
              />
            </React.Fragment>
          ))}
          <span
            className="inline-flex items-center justify-start min-w-0 max-w-full"
            style={{ width: `calc(${nameSlotWidthCh}ch + 26px)`, maxWidth: 'min(28rem, 100%)' }}
          >
            {editingSchemaName ? (
              <InlineNameEditor
                value={name}
                className="schema-name-chip schema-name-chip--input text-xs font-semibold"
                onSave={commitSchemaNameEdit}
                onCancel={cancelSchemaNameEdit}
                onDraftChange={setRenameDraft}
              />
            ) : (
              <button
                type="button"
                onClick={startSchemaNameEdit}
                className="schema-name-chip w-full min-w-0 font-semibold truncate text-left m-0 max-w-full"
                title={t('schemas.rename')}
              >
                {breadcrumbDisplayLabel}
              </button>
            )}
          </span>
        </div>
        <CompactSelectPicker
          value={rootType}
          options={ROOT_TYPE_OPTIONS}
          className="schema-root-type-picker"
          menuMinWidth={96}
          aria-label={t('schemaEditor.rootType')}
          onChange={(v) => {
            setRootType(v);
            change({ rootType: v });
          }}
        />
        <div className="schema-editor-header-spacer" />
        <button
          type="button"
          className="mock-add-btn"
          onClick={() => {
            setImportError('');
            setImportOpen(true);
          }}
        >
          <FileJson size={14} /> {t('schemaEditor.import')}
        </button>
        <button
          type="button"
          className={`mock-add-btn${previewOpen ? ' mock-add-btn--active' : ''}`}
          onClick={togglePreview}
        >
          <Eye size={14} /> {t('schemaEditor.preview')}
        </button>
        <button
          type="button"
          className="mock-add-btn"
          onClick={handleSave}
        >
          <Save size={14} /> {t('schemaEditor.save')}
        </button>
      </div>

      <div className="schema-editor-body">
        {previewOpen ? (
          <ResizableSplitter
            direction="vertical"
            initialSplit={58}
            minSize={30}
            maxSize={80}
            minSecondPanelPx={120}
            className="schema-editor-split"
            onResizeEnd={() => setSplitLayoutTick((n) => n + 1)}
          >
            {tableContent}
            <div className="schema-preview-panel">
              <div className="schema-preview-header">
                <span className="schema-preview-title">{t('schemaEditor.previewTitle')}</span>
                <div className="schema-preview-actions">
                  <button
                    type="button"
                    className="mock-iconbtn"
                    title={t('schemaEditor.regenerate')}
                    onClick={refreshPreview}
                  >
                    <RefreshCw size={14} />
                  </button>
                  <button
                    type="button"
                    className="mock-iconbtn"
                    title={t('schemaEditor.closePreview')}
                    onClick={() => setPreviewOpen(false)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div className="schema-preview-editor-wrap">
                <CodeEditor
                  value={previewJson}
                  onChange={() => {}}
                  language="json"
                  readOnly
                  hideChrome
                  height="100%"
                  splitLayoutTick={splitLayoutTick}
                />
              </div>
            </div>
          </ResizableSplitter>
        ) : (
          tableContent
        )}
      </div>

      <Modal
        isOpen={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportError('');
        }}
        title={t('schemaEditor.importTitle')}
        subtitle={t('schemaEditor.importHint')}
        size="lg"
      >
        <textarea
          className="schema-import-textarea mock-input w-full"
          rows={12}
          value={importText}
          placeholder={t('schemaEditor.importPlaceholder')}
          onChange={(e) => {
            setImportText(e.target.value);
            setImportError('');
          }}
        />
        {importError ? (
          <p className="schema-import-error">{importError}</p>
        ) : null}
        <div className="flex gap-2 mt-4">
          <button
            type="button"
            className="modern-button flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            onClick={handleImport}
          >
            {t('schemaEditor.importConfirm')}
          </button>
          <button
            type="button"
            className="modern-button flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            onClick={() => {
              setImportOpen(false);
              setImportError('');
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      </Modal>

      {activeDragId && (
        <div
          ref={overlayRef}
          style={{
            position: 'fixed',
            left: dragX,
            top: dragY,
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <div className="schema-drag-overlay">
            <GripVertical size={14} className="shrink-0 text-gray-400" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {draggedNodeName || t('schemaEditor.fieldName')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaEditor;
